import { html } from "lit";
import { formatHtml } from "@trumpf/ux.common-storybook";
import { StatusType } from "@trumpf/ux.ui-widgets/dist/types/components/status/status-types";
import { OverviewHeaderIntroRow } from "../../../partial-templates/overview-header/overview-header.stories";
import { addI18nTranslations } from "../../../../utils";
import { renderPageTemplate } from "../../../../utils";

// DEPRECATED: These are the old software update status labels that are kept for backwards compatibility
const subscribed: StatusType = "SOFTWARE_UPDATES_SUBSCRIBED";
const unsubscribed: StatusType = "SOFTWARE_UPDATES_UNSUBSCRIBED";
const installationFailed: StatusType = "SOFTWARE_UPDATES_INSTALLATION_FAILED";
const updateAvailable: StatusType = "SOFTWARE_UPDATES_UPDATE_AVAILABLE";
const notAvailableForSubscription: StatusType =
    "SOFTWARE_UPDATES_NOT_AVAILABLE_FOR_SUBSCRIPTION";

// Currently used status labels of V2
const UpdatesNotSubscribed: StatusType =
    "SOFTWARE_UPDATES_UpdatesNotSubscribed";
const UpdatesPartiallySubscribed: StatusType =
    "SOFTWARE_UPDATES_UpdatesPartiallySubscribed";
const AllUpdatesSubscribed: StatusType =
    "SOFTWARE_UPDATES_AllUpdatesSubscribed";
const WaitingForInstallationOfSecurityUpdate: StatusType =
    "SOFTWARE_UPDATES_WaitingForInstallationOfSecurityUpdate";
const WaitingForInstallationOfFunctionalUpdate: StatusType =
    "SOFTWARE_UPDATES_WaitingForInstallationOfFunctionalUpdate";
const InstallationFailedOfSecurityUpdate: StatusType =
    "SOFTWARE_UPDATES_InstallationFailedOfSecurityUpdate";
const InstallationFailedOfFunctionalUpdate: StatusType =
    "SOFTWARE_UPDATES_InstallationFailedOfFunctionalUpdate";

const softwareUpdateLabels = {
    [subscribed]: "Updates subscribed",
    [unsubscribed]: "Updates not subscribed",
    [installationFailed]: "Installation failed",
    [updateAvailable]: "Update available",
    [notAvailableForSubscription]: "Not available for subscription",
    [UpdatesNotSubscribed]: "Updates nicht abonniert",
    [UpdatesPartiallySubscribed]: "Updates teilweise abonniert",
    [AllUpdatesSubscribed]: "Updates abonniert",
    [WaitingForInstallationOfSecurityUpdate]: "Wartet auf Installation",
    [WaitingForInstallationOfFunctionalUpdate]: "Wartet auf Installation",
    [InstallationFailedOfSecurityUpdate]: "Installation fehlgeschlagen",
    [InstallationFailedOfFunctionalUpdate]: "Installation fehlgeschlagen",
};

const namespaceMyMachine = "my-machine";
const translationsMyMachine = {
    filter: {
        isFav: { label: "Nur Favoriten" },
        location: {
            placeholder: "Standort",
            searchPlaceholder: "Suchbegriff eingeben",
        },
        technology: {
            placeholder: "Technologie",
            searchPlaceholder: "Suchbegriff eingeben",
        },
    },
    content: {
        checkboxValue: { column: "" },
        isFav: { column: "Favorit" },
        name: { column: "Maschine" },
        number: { column: "Maschinennummer" },
        constructionYear: { column: "Baujahr" },
        subEquipments: { column: "Ausstattung", label: "Ausstattung" },
        location: { column: "Standort" },
        softwareUpdates: {
            column: "Software Updates",
            values: softwareUpdateLabels,
        },
        detailLink: { column: "Details" },
        disabledCreateCase: {
            headerText: "Fehlende Berechtigung",
            bodyText:
                "Sie besitzen nicht die notwendige Berechtigung zum Erstellen eines Servicefalls. Bei Bedarf, wenden Sie sich bitte an Ihren MyTRUMPF Unternehmensadministrator.",
        },
        error: {
            headline: "Etwas ist schief gelaufen",
            text: "Bitte versuchen Sie es erneut oder kontaktieren Sie Ihren Support.",
        },
        filterError: {
            headline:
                "F\u00fcr die Ausgew\u00e4hlten Filter gibt es keine Treffer.",
            text: "Bitte \u00e4ndern Sie Ihre Filtereinstellung, um fortzufahren.",
        },
    },
};

const namespaceEcat = "ecat-stateful";
const translationsEcat = {
    content: {
        check: {
            column: "Auswahl",
        },
        machine: {
            column: "Maschinenname",
        },
        machineNum: {
            column: "Maschinennummer",
        },
        location: {
            column: "Standort",
        },
    },
};

addI18nTranslations({
    namespace: namespaceMyMachine,
    language: "de-DE",
    translations: translationsMyMachine,
});

addI18nTranslations({
    namespace: namespaceEcat,
    language: "de-DE",
    translations: translationsEcat,
});

const Template = (args) => {
    const myMachineOverviewTemplate = (url) => html`
        <div class="ux-t-container">
            ${OverviewHeaderIntroRow({
                type: "my-machine",
                headline: "My Machines",
                buttonLabel: "Add machine",
                buttonLabelEcat: "Request E-Catalog",
            })}
            <ux-sidebar
                name="filter-sidebar-1"
                close-on-esc-key="true"
                close-on-backdrop-click="true"
                transition="slide"
                alignment="right"
                disable-backdrop="false"
                disable-close-icon="false"
                align-header="false"
                align-footer="false"
                opened="false"
            >
                <ux-sidebar-content
                    class="ux-u-pb-xs ux-u-px-xs ux-u-px-md-l ux-u-mt-xxs"
                >
                    <ux-iws-stateful-filter
                        url="generated-mock-data/mymachine-stateful-filter.json"
                        scope="my-machine"
                        filter-column-class="ux-col-12 ux-u-mb-xs"
                        disable-tags
                        instant-filtering
                    >
                    </ux-iws-stateful-filter>
                </ux-sidebar-content>
                <ux-sidebar-footer>
                    <ux-button
                        class="ux-u-mb-xxs ux-u-mx-zero ux-u-mx-md-xs"
                        variant="primary"
                        class="ux-button--full-width"
                        data-trigger-sidebar="toggle,filter-sidebar-1"
                        >Treffer anzeigen</ux-button
                    >
                    <ux-iws-stateful-filter-remove-button
                        class="ux-u-mx-zero ux-u-mx-md-xs"
                        variant="secondary"
                        scope="my-machine"
                    ></ux-iws-stateful-filter-remove-button>
                </ux-sidebar-footer>
            </ux-sidebar>
        </div>
        <div class="ux-t-container-full">
            <div class="ux-row u-background-blue-gradient ux-u-shadow-inset">
                <div class="ux-col-10 ux-u-offset-1 ux-u-mt-xs">
                    <ux-iws-stateful-filter-button
                        scope="my-machine"
                        data-trigger-sidebar="toggle,filter-sidebar-1"
                        >Filter</ux-iws-stateful-filter-button
                    >
                    <ux-iws-stateful-filter-tags
                        scope="my-machine"
                        class="ux-u-d-none ux-u-d-lg-block"
                    >
                    </ux-iws-stateful-filter-tags>
                </div>
                <ux-iws-stateful-overview
                    url="${url}"
                    scope="my-machine"
                    debug="true"
                    switch="true"
                    view="table"
                    render-test-ids
                >
                </ux-iws-stateful-overview>
            </div>
        </div>
        <ux-u-silent-auth
            client-id="1kffflasH4444COTiFmP8DDO9ywa"
            client-name="mytrumpf"
            issuer="https://identity-s.trumpf.com"
            scope="openid"
        >
        </ux-u-silent-auth>
    `;

    return html`
        ${args.response === "Default"
            ? html`
                  ${myMachineOverviewTemplate(
                      "generated-mock-data/mymachine-stateful.json"
                  )}
              `
            : ""}
        ${args.response === "Empty"
            ? html`
                  ${myMachineOverviewTemplate(
                      "generated-mock-data/mymachine-stateful-empty.json"
                  )}
              `
            : ""}
        ${args.response === "Filtered Empty"
            ? html`
                  ${myMachineOverviewTemplate(
                      "generated-mock-data/mymachine-stateful-filtered-empty.json"
                  )}
              `
            : ""}
        ${args.response === "Service Case Creation Disabled"
            ? html`
                  ${myMachineOverviewTemplate(
                      "generated-mock-data/mymachine-stateful-case-creation-disabled.json"
                  )}
              `
            : ""}
    `;
};

export default {
    title: "MyTrumpf/Page Templates/My Machines/Overview",
    parameters: {
        docs: {
            source: {
                transform: formatHtml,
            },
        },
        layout: "fullscreen",
    },
    args: {
        response: "Default",
    },
    argTypes: {
        response: {
            options: [
                "Default",
                "Empty",
                "Filtered Empty",
                "Service Case Creation Disabled",
            ],
            control: { type: "radio" },
        },
    },
};

export const Default = (args) => renderPageTemplate(() => Template(args));
Default.translationsMyMachine = translationsMyMachine;
Default.namespaceMyMachine = namespaceMyMachine;

Default.translationsEcat = translationsEcat;
Default.namespaceEcat = namespaceEcat;
